import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useMemo } from "react";
import { sorted } from "src/helpers";
import { LocationExceptionalStatusRead } from "src/store/api/generatedApi";

dayjs.extend(utc);

const overlapsDay = (
  status: LocationExceptionalStatusRead,
  dayStart: Dayjs,
  dayEnd: Dayjs
): boolean =>
  dayjs.utc(status.started_at).isBefore(dayEnd) &&
  dayjs.utc(status.ended_at).isAfter(dayStart);

// Area-targeted exceptions (broken_scale_weights) are owned by the standalone
// Exceptions page; this panel covers location-scoped exceptional periods only.
const isLocationScoped = (status: LocationExceptionalStatusRead): boolean =>
  status.area === null;

export const useDayExceptions = ({
  exceptionalStatuses,
  dayStart,
  dayEnd,
}: {
  exceptionalStatuses: LocationExceptionalStatusRead[];
  dayStart: Dayjs;
  dayEnd: Dayjs;
}): LocationExceptionalStatusRead[] =>
  useMemo(
    () =>
      sorted(
        exceptionalStatuses.filter(
          (status) =>
            isLocationScoped(status) && overlapsDay(status, dayStart, dayEnd)
        ),
        (status) => dayjs.utc(status.started_at).valueOf()
      ),
    [exceptionalStatuses, dayStart, dayEnd]
  );

export const useOverlappingExceptions = ({
  exceptionalStatuses,
  startedAt,
  endedAt,
  excludeId,
}: {
  exceptionalStatuses: LocationExceptionalStatusRead[];
  startedAt: Dayjs | null;
  endedAt: Dayjs | null;
  excludeId?: number;
}): LocationExceptionalStatusRead[] =>
  useMemo(() => {
    if (startedAt === null || endedAt === null) return [];
    if (!startedAt.isBefore(endedAt)) return [];
    const startMs = startedAt.valueOf();
    const endMs = endedAt.valueOf();
    return sorted(
      exceptionalStatuses.filter(
        (status) =>
          isLocationScoped(status) &&
          status.id !== excludeId &&
          dayjs.utc(status.started_at).valueOf() < endMs &&
          dayjs.utc(status.ended_at).valueOf() > startMs
      ),
      (status) => dayjs.utc(status.started_at).valueOf()
    );
  }, [exceptionalStatuses, startedAt, endedAt, excludeId]);
